/**
 * Format currency in Indonesian Rupiah format
 * @param amount - Amount in IDR
 * @returns Formatted string like "Rp 100.000"
 */
export function formatCurrency(amount: number): string {
  return `Rp ${amount.toLocaleString('id-ID')}`
}

/**
 * Format date in Indonesian format
 * @param date - Date string or Date object
 * @returns Formatted string like "17 November 2025"
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/**
 * Format phone number in Indonesian format
 * @param phone - Phone number string
 * @returns Formatted string like "0812-3456-7890"
 */
export function formatPhone(phone: string): string {
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '')
  
  // Format as 0812-3456-7890
  if (digits.startsWith('62')) {
    // Convert +62 to 0
    const local = '0' + digits.slice(2)
    return local.replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3')
  }
  
  return digits.replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3')
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate Indonesian phone number
 */
export function isValidPhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, '')
  // Must start with 0 or 62 and be 10-13 digits
  return /^(0|62)\d{9,12}$/.test(digits)
}

/**
 * Calculate cart totals
 */
export function calculateCartTotals(
  items: Array<{ price: number; quantity: number }>,
  taxRate: number,
  shippingCost: number,
): {
  subtotal: number
  taxAmount: number
  shippingCost: number
  total: number
} {
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const taxAmount = Math.round(subtotal * (taxRate / 100))
  const total = subtotal + taxAmount + shippingCost

  return {
    subtotal,
    taxAmount,
    shippingCost,
    total,
  }
}

/**
 * Generate WhatsApp URL for order
 */
export function generateWhatsAppUrl(
  phone: string,
  message: string,
): string {
  const cleanPhone = phone.replace(/\D/g, '')
  const encodedMessage = encodeURIComponent(message)
  return `https://wa.me/${cleanPhone}?text=${encodedMessage}`
}

/**
 * Combine class names
 */
export function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ')
}
